/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { yupResolver } from '@hookform/resolvers/yup';
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitErrorHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Holiday, ProductionMasters, ScheduleDetail, ScheduleDivision } from '../../../../api/model';
import {
  useGetScheduleDetail,
  usePostScheduleCalculate,
  usePostScheduleCancel,
  usePostScheduleLock,
  usePostScheduleNotify,
  usePostScheduleSave,
} from '../../../../api/schedules/schedules';
import { useDefaultOptions } from '../../../../hooks/api';
import { postWithKeepAlive } from '../../../../utils/api';
import yup from '../../../../utils/custom-yup';
import { CheckBox } from '../../../atoms/CheckBox';
import DatePicker from '../../../atoms/DatePicker';
import ErrorComponent from '../../../atoms/ErrorComponent';
import Link from '../../../atoms/Link';
import NoDataComponent from '../../../atoms/NoDataComponent';
import SingleSelect from '../../../atoms/SingleSelect';
import WaitComponent from '../../../atoms/WaitComponent';
import AnotherTabLockingErrorModal from '../../../modules/AnotherTabLockingErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import { CancelButton } from './FormElements';
import SubScheduleFormModal from './schedule/SubScheduleFormModal';

interface MainAreaProps {
  productionId: number;
  productionDivisionId: number;
  data: ScheduleDetail;
  scheduleDevisions: ScheduleDivision[];
  holidays: Holiday[];
  editModeTabIndex: number | undefined;
  setEditable: (editable: boolean) => void;
  refetch: () => Promise<void>;
}

interface MainAreaHandler {
  refresh: () => void;
}

/**
 * 日程編集タブのメイン部分
 */
const MainArea = forwardRef<MainAreaHandler, MainAreaProps>(
  (
    { productionId, productionDivisionId, data, scheduleDevisions, holidays, editModeTabIndex, setEditable, refetch },
    ref
  ) => {
    // 言語情報
    const {
      t,
      i18n: { language },
    } = useTranslation();

    // バリデーション用のスキーマ
    const schema = yup.object().shape({
      production_id: yup.number().required(),
      schedule_division_id: yup
        .number()
        .nullable()
        .transform((v) => (v || v === 0 ? Number(v) : undefined))
        .default(undefined),
      mass_production_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      previous_mass_production_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      two_times_before_mass_production_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      three_times_before_mass_production_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      release_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      previous_release_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      two_times_before_release_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      three_times_before_release_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      updated_on: yup
        .string()
        .date()
        .nullable()
        .transform((v) => (v ? String(v) : undefined))
        .default(undefined),
      pg2_duplicate_check: yup
        .object()
        .nullable()
        .transform(() => undefined)
        .default(undefined),
      schedule_items: yup
        .array()
        .required()
        .of(
          yup.object().shape({
            schedule_item_id: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            schedule_master_item_id: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            not_applicable: yup.boolean().required(),
            process: yup
              .string()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            milestone: yup
              .string()
              .nullable()
              .transform((v) => (v ? String(v) : undefined)),
            person_in_charge: yup
              .string()
              .nullable()
              .transform((v) => (v ? String(v) : undefined)),
            period: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            plan_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            plan_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            actual_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            actual_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            previous_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            previous_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            two_times_before_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            two_times_before_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            three_times_before_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            three_times_before_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
          })
        ),
      sub_schedule_items: yup
        .array()
        .required()
        .of(
          yup.object().shape({
            sub_schedule_item_id: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            milestone: yup.string().required(),
            notification_timing: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            destination: yup
              .number()
              .nullable()
              .transform((v) => (v || v === 0 ? Number(v) : undefined))
              .default(undefined),
            message: yup
              .string()
              .nullable()
              .transform((v) => (v ? String(v) : undefined)),
            plan_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            plan_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            actual_from_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
            actual_end_on: yup
              .string()
              .date()
              .nullable()
              .transform((v) => (v ? String(v) : undefined))
              .default(undefined),
          })
        ),
    });

    // フォームを利用できるようにする
    const { register, handleSubmit, control, getValues, reset } = useForm({
      defaultValues: data,
      resolver: yupResolver(schema),
    });

    // データが変更されたらリセットする
    useEffect(() => reset(data), [reset, data]);

    // 日程項目一覧
    const {
      fields: scheduleItemsFields,
      replace: replaceScheduleItems,
      update: updateScheduleItems,
    } = useFieldArray({
      control,
      name: 'schedule_items',
    });

    // サブ日程項目一覧
    const {
      fields: subScheduleItemsFields,
      append: appendSubScheduleItems,
      update: updateSubScheduleItems,
      replace: replaceSubScheduleItems,
      remove: removeSubScheduleItems,
    } = useFieldArray({
      control,
      name: 'sub_schedule_items',
    });

    // 編集状態かどうか
    const editable = useMemo(() => editModeTabIndex === 2, [editModeTabIndex]);

    // スピナーモーダルを表示する
    const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

    // ロックを取得する setInterval の返す ID
    const refLockIntervalId = useRef<NodeJS.Timer>();
    useEffect(() => () => clearInterval(refLockIntervalId.current), []);

    // beforeUnloadEvent（ブラウザを閉じようとしたときに、メッセージ表示）
    const beforeUnload = useCallback((e: BeforeUnloadEvent) => {
      e.returnValue = '';
    }, []);

    // コンポーネントが表示されなくなる時に、beforeunload イベントの解除
    useEffect(() => () => window.removeEventListener('beforeunload', beforeUnload), [beforeUnload]);

    // unload 関数（ブラウザを閉じるときに発生して、ロック解除するためのもの）
    const unload = useCallback(async () => {
      await postWithKeepAlive(`/schedules/cancel/${productionId}`, language);
    }, [language, productionId]);

    const refUnload = useRef<() => Promise<void>>();

    // コンポーネントが表示されなくなる時に、unload イベントの実行と解除
    useEffect(
      () => () => {
        if (refUnload.current != null) {
          void refUnload.current().then(() => {
            if (refUnload.current != null) {
              window.removeEventListener('unload', refUnload.current);
            }
          });
        }
      },
      []
    );

    // #region ロック関連

    // ロック API
    const options = useDefaultOptions(language);
    const { mutateAsync: lockAsync, error: lockError } = usePostScheduleLock(options);

    // ロックエラーを表示するかどうか
    const [displayLockError, setDisplayLockError] = useState(false);

    // ロックエラーが設定された場合は表示
    useEffect(() => {
      setDisplayLockError(lockError != null);
      setDisplaySpinnerModal(false);
    }, [lockError]);

    // 他のタブがロックされていることのエラー
    const [displayAnotherTabLockError, setDisplayAnotherTabLockError] = useState(false);

    // 編集をやめる
    const onFinishEditing = useCallback(() => {
      // 編集モードをやめる
      setEditable(false);

      // ロックの定期実行をやめる
      clearInterval(refLockIntervalId.current);

      // ブラウザを閉じた時などのイベントを解除
      window.removeEventListener('beforeunload', beforeUnload);
      if (refUnload.current != null) {
        window.removeEventListener('unload', refUnload.current);
      }

      // フォームをリセット
      reset(data);

      // スピナーモーダルを非表示
      setDisplaySpinnerModal(false);
    }, [beforeUnload, data, reset, setEditable]);

    // 編集モードにする
    const edit = useCallback(async () => {
      // 自身が編集モードのときは何もしない
      if (editModeTabIndex === 2) return;

      // 編集モードのタブがある場合はエラー表示
      if (editModeTabIndex != null) {
        setDisplayAnotherTabLockError(true);
        return;
      }

      // スピナーモーダルの表示
      setDisplaySpinnerModal(true);

      // 成功した場合の処理
      const onSuccess = async () => {
        // 表示するデータをリフェッチする
        try {
          await refetch();

          // 定期的にロック API をたたく
          const lockInterval = parseInt(process.env.REACT_APP_GET_LOCK_INTERVAL ?? '10000', 10);
          refLockIntervalId.current = setInterval(() => {
            void lockAsync({ productionId }, { onError: onFinishEditing });
          }, lockInterval);

          // ブラウザを閉じた時などのロック解除を設定
          window.addEventListener('beforeunload', beforeUnload);
          refUnload.current = unload;
          window.addEventListener('unload', refUnload.current);

          // 編集モードにする
          setEditable(true);
        } catch {
          // Do nothing
        } finally {
          // スピナーモーダルを非表示に
          setDisplaySpinnerModal(false);
        }
      };

      // 失敗時の処理（スピナーモーダルを非表示に）
      const onError = () => setDisplaySpinnerModal(false);

      // ロック API にアクセス
      await lockAsync({ productionId }, { onSuccess, onError });
    }, [beforeUnload, editModeTabIndex, lockAsync, onFinishEditing, productionId, refetch, setEditable, unload]);

    // #endregion

    // #region キャンセル関連

    // キャンセル API
    const { mutateAsync: cancelAsync, error: cancelError } = usePostScheduleCancel(options);

    // キャンセルエラーを表示するかどうか
    const [displayCancelError, setDisplayCancelError] = useState(false);

    // キャンセルエラーが設定された場合は表示
    useEffect(() => {
      setDisplayCancelError(cancelError != null);
      setDisplaySpinnerModal(false);
    }, [cancelError]);

    // キャンセルエラーが設定された場合は表示
    useEffect(() => {
      setDisplayCancelError(cancelError != null);
      setDisplaySpinnerModal(false);
    }, [cancelError]);

    // 編集モードをキャンセル
    const cancel = useCallback(async () => {
      // スピナーモーダルの表示
      setDisplaySpinnerModal(true);

      // 成功時の処理（スピナーモーダルを非表示に）
      const onError = () => setDisplaySpinnerModal(false);

      // ロックキャンセル API をたたく
      await cancelAsync({ productionId }, { onSuccess: onFinishEditing, onError });
    }, [cancelAsync, productionId, onFinishEditing]);

    // #endregion

    // #regin 保存ボタン押下時の動作

    // 保存確認メッセージを表示するかどうか
    const [displayConfirmSavingMessage, setDisplayConfirmSavingMessage] = useState(false);

    // 保存確認
    const confirmSaving = useCallback(async () => {
      const onValid = () => setDisplayConfirmSavingMessage(true);
      await handleSubmit(onValid)();
    }, [handleSubmit]);

    // 保存 API
    const { mutateAsync: saveAsync, error: saveError } = usePostScheduleSave(options);

    // 保存時エラーを表示するかどうか
    const [displaySaveError, setDisplaySaveError] = useState(false);

    // 保存時エラーが設定された場合は表示
    useEffect(() => {
      setDisplaySaveError(saveError != null);
      setDisplaySpinnerModal(false);
    }, [saveError]);

    // バリデーションエラーを表示するかどうか
    const [displayValidationError, setDisplayValidationError] = useState(false);

    // 保存完了メッセージ表示するかどうか
    const [displayCompleteSavingMessage, setDisplayCompleteSavingMessage] = useState(false);

    // 保存ボタン押下時の動作
    const submit = useCallback(async () => {
      // 確認モーダルを非表示
      setDisplayConfirmSavingMessage(false);

      // スピナーモーダルを表示
      setDisplaySpinnerModal(true);

      // バリデーション結果が有効だった場合
      const onValid = async (nextData: ScheduleDetail) => {
        // 成功した場合の処理
        const onSuccess = async () => {
          // ブラウザを閉じた時などのイベントを解除
          window.removeEventListener('beforeunload', beforeUnload);
          if (refUnload.current != null) {
            window.removeEventListener('unload', refUnload.current);
          }

          try {
            // 保存成功したらリフェッチする
            await refetch();

            // 編集状態を解除
            setEditable(false);

            // ロック API の呼び出しを解除
            clearInterval(refLockIntervalId.current);

            // 保存完了したことをメッセージ表示
            setDisplayCompleteSavingMessage(true);
          } catch {
            // Do nothing
          } finally {
            setDisplaySpinnerModal(false);
          }
        };

        // 成功失敗共通の処理（スピナーモーダルを非表示に）
        const onError = () => setDisplaySpinnerModal(false);

        // 保存実行
        await saveAsync(
          {
            data: nextData,
          },
          { onSuccess, onError }
        );
      };

      // バリデーション結果が無効だった場合（エラーモーダルを表示）
      const onInvalid: SubmitErrorHandler<ScheduleDetail> = () => {
        // スピナーモーダルを非表示
        setDisplaySpinnerModal(false);

        // バリデーションエラーをモーダルで表示
        setDisplayValidationError(true);
      };

      // フォーム送信時の処理を設定
      await handleSubmit(onValid, onInvalid)();
    }, [beforeUnload, handleSubmit, refetch, saveAsync, setEditable]);

    // #endregion

    // サブ日程は量産日発売日と組み合わせて表示するため、最低二行必要
    const subScheduleItemsForDisplay = useMemo(() => {
      if (subScheduleItemsFields.length === 0) {
        return [undefined, undefined];
      }
      if (subScheduleItemsFields.length === 1) {
        return [subScheduleItemsFields[0], undefined];
      }
      return subScheduleItemsFields;
    }, [subScheduleItemsFields]);

    // 日程変更通知
    const { mutateAsync: _notifyAsync, error: notifyError, isError: notifyIsError } = usePostScheduleNotify(options);

    // 通知確認モーダル
    const [displayConfirmNotifying, setDisplayConfirmNotifying] = useState(false);

    // 通知完了モーダル
    const [displayCompleteNotifying, setDisplayCompleteNotifying] = useState(false);

    // 通知エラーモーダル
    const [displayNotifyError, setDisplayNotifyError] = useState(false);

    useEffect(() => {
      setDisplayNotifyError(notifyIsError);
    }, [notifyIsError]);

    // 通知実行
    const notifyAsync = useCallback(async () => {
      // 通知確認モーダルを非表示
      setDisplayConfirmNotifying(false);

      // スピナーモーダルを表示
      setDisplaySpinnerModal(true);

      // 通知実行
      await _notifyAsync(
        { productionId },
        {
          // 成功時
          onSuccess: () => {
            // スピナーモダールを非表示
            setDisplaySpinnerModal(false);

            // 完了メッセージを表示
            setDisplayCompleteNotifying(true);
          },
          // 失敗時
          onError: () => {
            // スピナーモダールを非表示
            setDisplaySpinnerModal(false);
          },
        }
      );
    }, [_notifyAsync, productionId]);

    // 自動計算 API
    const {
      mutateAsync: _calculateAsync,
      data: calculateData,
      error: calculateError,
      isError: calculateIsError,
    } = usePostScheduleCalculate(options);

    // 自動計算エラー
    const [displayCalculateError, setDisplayCalculateError] = useState(false);

    useEffect(() => {
      setDisplayCalculateError(calculateIsError);
    }, [calculateIsError]);

    // 自動計算バリデーションエラー
    const [displayCalculateValidationError, setDisplayCalculateValidationError] = useState<{
      scheduleDivision: boolean;
      massProductionOn: boolean;
      v3On: boolean;
    }>();

    // 量産日を超えた場合に確認メッセージ
    const [displayReflectCaluclationWarning, setDisplayReflectCalculationWarning] = useState(false);

    // 自動計算実行
    const calculateAsync = useCallback(async () => {
      const values = getValues();
      const scheduleDivisionId = values.schedule_division_id;
      let v3On: string | undefined;
      if (values.sub_schedule_items != null) {
        for (let i = 0; i < values.sub_schedule_items.length; i += 1) {
          const item = values.sub_schedule_items[i];
          if (item.milestone === 'V3日程') {
            v3On = item.plan_from_on;
            break;
          }
        }
      }
      const massProductionOn = values.mass_production_on;
      const releaseOn = values.release_on;
      const scheduleItems = values.schedule_items;

      if (scheduleDivisionId != null && massProductionOn != null && v3On != null) {
        // 日程区分、量産日、V3 日程は必須

        // スピナーモーダルの表示
        setDisplaySpinnerModal(true);

        // 自動計算本体
        await _calculateAsync(
          {
            productionId,
            data: {
              schedule_division_id: scheduleDivisionId,
              v3_on: v3On,
              mass_production_on: massProductionOn,
              release_on: releaseOn,
              schedule_items: scheduleItems,
            },
          },
          {
            onSuccess: (response) => {
              // 量産日を超えるマイルストーンがあった場合に確認メッセージを表示
              if (response.data.too_late === true) {
                // スピナーモーダルを非表示に
                setDisplaySpinnerModal(false);

                // 確認モーダルを表示
                setDisplayReflectCalculationWarning(true);
                return;
              }

              // 返り値で日程項目を入れ替える
              replaceScheduleItems(response.data.schedule_items ?? []);

              // スピナーモーダルを非表示に
              setDisplaySpinnerModal(false);
            },
            onError: () => {
              // スピナーモーダルを非表示に
              setDisplaySpinnerModal(false);
            },
          }
        );
      } else {
        // 日程区分、量産日、V3 日程が入力されていない場合エラー表示
        setDisplayCalculateValidationError({
          scheduleDivision: scheduleDivisionId == null,
          massProductionOn: massProductionOn == null,
          v3On: v3On == null,
        });
      }
    }, [getValues, _calculateAsync, productionId, replaceScheduleItems]);

    // サブ日程の追加編集用モーダル（undefined: 非表示、null: 追加モード、その他: 編集モード）
    const [addOrEditTargetSubScheduleIndex, setAddOrEditTargetSubScheduleIndex] = useState<number | null>();

    // 外部に公開する処理
    useImperativeHandle(ref, () => ({
      refresh: () => {
        reset(data);
      },
    }));

    return (
      <form>
        {/* タブ内のヘッダー部分 */}
        <div className="producfr-navi">
          <div className="dropdown-in">
            <div className="droplist3">
              <span className="dropdown">
                <SingleSelect
                  name="schedule_division_id"
                  allowEmpty
                  options={scheduleDevisions}
                  register={register}
                  getValue={(x) => x.schedule_division_id}
                  getLabel={(x) => x.schedule_division_name}
                  isDisabled={!editable}
                  onChange={(e) => {
                    scheduleItemsFields.forEach((x, i) => {
                      const nextId = parseInt(e.target.value, 10);
                      const period = data.schedule_master_periods?.find(
                        (y) => y.schedule_master_item_id === x.schedule_master_item_id
                      );
                      if (period != null) {
                        if (nextId === 1) {
                          updateScheduleItems(i, { ...x, period: period.period_fmc_with_ka });
                        } else if (nextId === 2) {
                          updateScheduleItems(i, { ...x, period: period.period_fmc_without_ka });
                        } else if (nextId === 3) {
                          updateScheduleItems(i, { ...x, period: period.period_mmc_with_ka });
                        } else if (nextId === 4) {
                          updateScheduleItems(i, { ...x, period: period.period_mmc_without_ka });
                        } else {
                          updateScheduleItems(i, { ...x, period: undefined });
                        }
                      }
                    });
                  }}
                />
              </span>
            </div>
          </div>
          <ul>
            <li className="cursor">
              <button
                type="button"
                className="text-button"
                disabled={![1, 2, 3, 13].includes(productionDivisionId) || !editable}
                onClick={calculateAsync}
              >
                <i className="fa-solid fa-calculator" />
                {t('自動計算')}
              </button>
            </li>
            {editable ? (
              <>
                <li className="cursor detailbtn1-2b">
                  <button type="button" className="text-button" onClick={confirmSaving}>
                    <i className="fa-solid fa-floppy-disk" />
                    {t('保存')}
                  </button>
                </li>
                <li className="cursor">
                  <CancelButton editable={editable} cancel={cancel} />
                </li>
              </>
            ) : (
              <li className="cursor detailbtn1-2a">
                <button type="button" className="text-button" onClick={edit}>
                  <i className="fa-solid fa-pen-to-square" />
                  {t('編集')}
                </button>
              </li>
            )}
            <li className="updatetxt">
              {t('更新日：')}
              {data.updated_on?.replaceAll('-', '/')}
            </li>
          </ul>
        </div>
        {/* 日程項目テーブル */}
        <div className="tablest1">
          <div className="scroll no-scroll">
            <table className="table4">
              <thead>
                <tr>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td colSpan={2}>{t('予定')}</td>
                  <td colSpan={2}>{t('実績')}</td>
                  <td colSpan={2}>{t('1つ前')}</td>
                  <td colSpan={2}>{t('2つ前')}</td>
                  <td colSpan={2}>{t('3つ前')}</td>
                </tr>
                <tr>
                  <td>{t('対象外')}</td>
                  <td>
                    {t('工程')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('マイルストーン')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('担当')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('期間')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                </tr>
              </thead>
              <tbody>
                {scheduleItemsFields.map((x, i) => (
                  <tr key={x.id}>
                    <td>
                      <CheckBox
                        label={undefined}
                        name={`schedule_items.${i}.not_applicable`}
                        isDisabled={!editable}
                        register={register}
                        value={undefined}
                      />
                    </td>
                    <td>{x.process}</td>
                    <td>
                      {(x.milestone ?? '').split('\n').map((y, j) => (
                        <React.Fragment key={j}>
                          {j !== 0 && <br />}
                          {y}
                        </React.Fragment>
                      ))}
                    </td>
                    <td>{x.person_in_charge}</td>
                    <td>{x.period}</td>
                    <td>
                      <DatePicker
                        id={undefined}
                        name={`schedule_items.${i}.plan_from_on`}
                        isDisabled={!editable}
                        control={control}
                        displayMark={false}
                      />
                    </td>
                    <td>
                      <DatePicker
                        id={undefined}
                        name={`schedule_items.${i}.plan_end_on`}
                        isDisabled={!editable}
                        control={control}
                        displayMark={false}
                      />
                    </td>
                    <td>
                      <DatePicker
                        id={undefined}
                        name={`schedule_items.${i}.actual_from_on`}
                        isDisabled={!editable}
                        control={control}
                        displayMark={false}
                      />
                    </td>
                    <td>
                      <DatePicker
                        id={undefined}
                        name={`schedule_items.${i}.actual_end_on`}
                        isDisabled={!editable}
                        control={control}
                        displayMark={false}
                      />
                    </td>
                    <td>{x.previous_from_on?.replaceAll('-', '/')}</td>
                    <td>{x.previous_end_on?.replaceAll('-', '/')}</td>
                    <td>{x.two_times_before_from_on?.replaceAll('-', '/')}</td>
                    <td>{x.two_times_before_end_on?.replaceAll('-', '/')}</td>
                    <td>{x.three_times_before_from_on?.replaceAll('-', '/')}</td>
                    <td>{x.three_times_before_end_on?.replaceAll('-', '/')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="clearfix" />
        {/* サブ日程項目テーブル */}
        <div className="tablest2">
          <div>
            <table className="table5">
              <thead>
                <tr>
                  <td colSpan={2}>
                    <p className="dateadd">
                      <button
                        type="button"
                        className="text-button"
                        disabled={!editable}
                        onClick={() => {
                          if (editable) {
                            setAddOrEditTargetSubScheduleIndex(null);
                          }
                        }}
                      >
                        <i className="fa-solid fa-circle-plus" />
                        {t('日程追加')}
                      </button>
                    </p>
                  </td>
                  <td colSpan={2}>{t('予定')}</td>
                  <td className="wline" />
                  <td colSpan={2}>{t('実績')}</td>
                  <td className="wline" />
                  <td colSpan={5} />
                </tr>
                <tr>
                  <td />
                  <td />
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td className="wline" />
                  <td>
                    {t('From')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('End')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td className="wline" />
                  <td />
                  <td>
                    {t('新')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('1つ前')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('2つ前')}
                    <i className="fa-solid fa-sort" />
                  </td>
                  <td>
                    {t('3つ前')}
                    <i className="fa-solid fa-sort" />
                  </td>
                </tr>
              </thead>
              <tbody>
                {subScheduleItemsForDisplay.map((x, i) => {
                  let appendElement: ReactNode = null;

                  if (i === 0) {
                    appendElement = (
                      <>
                        <td className="wline"> </td>
                        <th>{t('量産日')}</th>
                        <td>
                          <DatePicker
                            id={undefined}
                            name="mass_production_on"
                            isDisabled={!editable}
                            control={control}
                            displayMark={false}
                          />
                        </td>
                        <td>{data.previous_mass_production_on?.replaceAll('-', '/')}</td>
                        <td>{data.two_times_before_mass_production_on?.replaceAll('-', '/')}</td>
                        <td>{data.three_times_before_mass_production_on?.replaceAll('-', '/')}</td>
                      </>
                    );
                  } else if (i === 1) {
                    appendElement = (
                      <>
                        <td className="wline"> </td>
                        <th>{t('発売日')}</th>
                        <td>
                          <DatePicker
                            id={undefined}
                            name="release_on"
                            isDisabled={!editable}
                            control={control}
                            displayMark={false}
                          />
                        </td>
                        <td>{data.previous_release_on?.replaceAll('-', '/')}</td>
                        <td>{data.two_times_before_release_on?.replaceAll('-', '/')}</td>
                        <td>{data.three_times_before_release_on?.replaceAll('-', '/')}</td>
                      </>
                    );
                  }
                  return (
                    <tr key={x?.id ?? -1 * i}>
                      {x == null ? (
                        <>
                          <td />
                          <th />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                        </>
                      ) : (
                        <>
                          <td>
                            {x.editable ? (
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  removeSubScheduleItems(i);
                                }}
                              >
                                {t('削除')}
                              </a>
                            ) : null}
                          </td>
                          <th>
                            {x.editable ? (
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setAddOrEditTargetSubScheduleIndex(i);
                                }}
                              >
                                {x.milestone}
                              </a>
                            ) : (
                              x.milestone
                            )}
                          </th>
                          <td>
                            <DatePicker
                              id={undefined}
                              name={`sub_schedule_items.${i}.plan_from_on`}
                              isDisabled={!editable}
                              control={control}
                              displayMark={false}
                            />
                          </td>
                          <td>
                            <DatePicker
                              id={undefined}
                              name={`sub_schedule_items.${i}.plan_end_on`}
                              isDisabled={!editable}
                              control={control}
                              displayMark={false}
                            />
                          </td>
                          <td className="wline" />
                          <td>
                            <DatePicker
                              id={undefined}
                              name={`sub_schedule_items.${i}.actual_from_on`}
                              isDisabled={!editable}
                              control={control}
                              displayMark={false}
                            />
                          </td>
                          <td>
                            <DatePicker
                              id={undefined}
                              name={`sub_schedule_items.${i}.actual_end_on`}
                              isDisabled={!editable}
                              control={control}
                              displayMark={false}
                            />
                          </td>
                        </>
                      )}
                      {appendElement}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="clearfix" />
        {/* サブ日程項目テーブル配下のコントロール */}
        <div className="schedule-control">
          <ul>
            <li className="cursor">
              <Link to={`/${language}/masterslist/schedule/11`}>
                <i className="fa-solid fa-file" />
                {t('日程マスターを編集')}
              </Link>
            </li>
            <li className="cursor">
              <button
                type="button"
                className="text-button"
                disabled={!editable}
                onClick={() => {
                  if (editable) {
                    const nextSubScheduleItems = getValues().sub_schedule_items ?? [];
                    for (let i = 0; i < nextSubScheduleItems.length; i += 1) {
                      nextSubScheduleItems[i].plan_from_on = undefined;
                      nextSubScheduleItems[i].plan_end_on = undefined;
                      nextSubScheduleItems[i].actual_from_on = undefined;
                      nextSubScheduleItems[i].actual_end_on = undefined;
                    }
                    replaceSubScheduleItems(nextSubScheduleItems);
                  }
                }}
              >
                <i className="fa-solid fa-circle-xmark" />
                {t('日程クリア')}
              </button>
            </li>
            {/* 日程変更の通知 */}
            <li className="cursor">
              <button
                type="button"
                className="text-button"
                disabled={editable}
                onClick={() => setDisplayConfirmNotifying(true)}
              >
                <i className="fa-solid fa-paper-plane" />
                {t('日程変更を通知')}
              </button>
            </li>
          </ul>
        </div>
        {/* G2 初回重複確認 */}
        <div className="pg2confirm ">
          <h3 className="mdh1">
            <span>{t('G2初回重複確認')}</span>
          </h3>
          <div className="scroll">
            <table className="table7">
              <tbody>
                {/* ヘッダー部 */}
                <tr>
                  <th />
                  {(data.g2_duplicate_check ?? []).map((x) => (
                    <th key={x.date}>{x.date?.replaceAll('-', '/')}</th>
                  ))}
                </tr>
                {/* G2開始予定日 */}
                <tr>
                  {/* 重複がある場合は、文字色を赤にする */}
                  <td
                    className={
                      (data.g2_duplicate_check ?? []).some((x) => (x.fmc ?? 0) + (x.mmc ?? 0) + (x.new ?? 0) > 0)
                        ? 'red-font'
                        : undefined
                    }
                  >
                    {t('G2開始予定日')}
                  </td>
                  {(data.g2_duplicate_check ?? []).map((x) => (
                    <td key={x.date}>{(x.fmc ?? 0) + (x.mmc ?? 0) + (x.new ?? 0) > 0 ? t('重複発生') : null}</td>
                  ))}
                </tr>
                {/* FMC(他機種予定) */}
                <tr>
                  <td>{t('FMC(他機種予定)')}</td>
                  {(data.g2_duplicate_check ?? []).map((x) => (
                    <td key={x.date}>{x.fmc}</td>
                  ))}
                </tr>
                {/* FMC(他機種予定) */}
                <tr>
                  <td>{t('MMC(他機種予定)')}</td>
                  {(data.g2_duplicate_check ?? []).map((x) => (
                    <td key={x.date}>{x.mmc}</td>
                  ))}
                </tr>
                {/* NEW(他機種予定) */}
                <tr>
                  <td>{t('NEW(他機種予定)')}</td>
                  {(data.g2_duplicate_check ?? []).map((x) => (
                    <td key={x.date}>{x.new}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/* 連休マスタ */}
        <div className="daysmaster">
          <h3 className="mdh1">
            <span>{t('連休マスター')}</span>
          </h3>
          <table className="table8">
            <tbody>
              <tr>
                <th> </th>
                <th>{t('年')}</th>
                <th>{t('分類')}</th>
                <th>{t('from')}</th>
                <th>{t('to')}</th>
              </tr>
              {holidays.map((x) => {
                const splited = x.from_on?.split('-');
                const year = splited != null && splited.length > 0 ? splited[0] : null;
                return (
                  <tr key={x.holiday_id}>
                    <td>{x.editing}</td>
                    <td>{year}</td>
                    <td>{x.category}</td>
                    <td>{x.from_on?.replaceAll('-', '/')}</td>
                    <td>{x.to_on?.replaceAll('-', '/')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* 連休マスタ下部コントロール */}
        <div className="schedule-control">
          <ul>
            <li className="cursor">
              <Link to={`/${language}/masterslist/edit/${data.holiday_master_version_id ?? ''}`}>
                <i className="fa-solid fa-file" />
                {t('連休マスターを編集')}
              </Link>
            </li>
            <li className="txt">{t('稼働日はカレンダーに準ずる')}</li>
          </ul>
        </div>
        {/* 日程の更新履歴 */}
        <div className="schedule-update mt30">
          <h3 className="mdh1">
            <span>{t('日程の更新履歴')}</span>
          </h3>
          <table className="table18">
            <tbody>
              <tr>
                <th>{t('工程')}</th>
                <th>{t('マイルストーン')}</th>
                <th>{t('区分')}</th>
                <th>{t('変更前')}</th>
                <th>{t('変更後')}</th>
                <th>{t('変更者')}</th>
                <th>{t('更新日')}</th>
              </tr>
              {(data.schedule_item_update_histories ?? []).map((x) => {
                let divisionName = t('予定From');
                if (x.division === 2) {
                  divisionName = t('予定End');
                } else if (x.division === 3) {
                  divisionName = t('実績From');
                } else if (x.division === 4) {
                  divisionName = t('実績End');
                }
                return (
                  <tr key={x.schedule_item_update_history_id}>
                    <td>{x.process}</td>
                    <td>{x.milestone}</td>
                    <td>{divisionName}</td>
                    <td>{x.before_change?.replaceAll('-', '/')}</td>
                    <td>{x.after_change?.replaceAll('-', '/')}</td>
                    <td>{x.update_user_name}</td>
                    <td>{x.updated_on}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* ロックエラー表示 */}
        {displayLockError && <ErrorModal error={lockError} onClickOk={() => setDisplayLockError(false)} />}
        {/* ロックキャンセルエラー表示 */}
        {displayCancelError && <ErrorModal error={cancelError} onClickOk={() => setDisplayCancelError(false)} />}
        {/* 保存時のエラー表示 */}
        {displaySaveError && <ErrorModal onClickOk={() => setDisplaySaveError(false)} error={saveError} />}
        {/* スピナーモーダル */}
        {displaySpinnerModal && <SpinnerModal />}
        {/* バリデーションエラーモーダル */}
        {displayValidationError && (
          <OkModal onClickOk={() => setDisplayValidationError(false)}>{t('バリデーションエラーです。')}</OkModal>
        )}
        {/* 他のタブがロックされている場合のエラー */}
        {displayAnotherTabLockError && editModeTabIndex != null && (
          <AnotherTabLockingErrorModal
            editModeTabIndex={editModeTabIndex}
            onClickOk={() => setDisplayAnotherTabLockError(false)}
          />
        )}
        {/* 日程変更の通知確認モーダル */}
        {displayConfirmNotifying && (
          <ConfirmModal
            onClickOk={notifyAsync}
            onClickCancel={() => {
              setDisplayConfirmNotifying(false);
            }}
          >
            <div>{t('日程情報を送付しますか？')}</div>
          </ConfirmModal>
        )}
        {/* 通知完了メッセージ */}
        {displayCompleteNotifying && (
          <OkModal onClickOk={() => setDisplayCompleteNotifying(false)}>
            <div>{t('送信しました。')}</div>
          </OkModal>
        )}
        {/* 通知エラーモーダル */}
        {displayNotifyError && <ErrorModal error={notifyError} onClickOk={() => setDisplayNotifyError(false)} />}
        {/* V3 日程未入力エラー */}
        {displayCalculateValidationError && (
          <OkModal onClickOk={() => setDisplayCalculateValidationError(undefined)}>
            {displayCalculateValidationError.scheduleDivision && <div>{t('日程区分が入力されていません。')}</div>}
            {displayCalculateValidationError.massProductionOn && <div>{t('量産日が入力されていません。')}</div>}
            {displayCalculateValidationError.v3On && <div>{t('V3日程が入力されていません。')}</div>}
          </OkModal>
        )}
        {/* 自動計算で量産日を超過するマイルストーンがあった場合のワーニング */}
        {displayReflectCaluclationWarning && (
          <ConfirmModal
            onClickOk={() => {
              // 確認モーダルを非表示
              setDisplayReflectCalculationWarning(false);

              // 返り値で日程項目を入れ替える
              replaceScheduleItems(calculateData?.data.schedule_items ?? []);

              // スピナーモーダルを非表示に
              setDisplaySpinnerModal(false);
            }}
            onClickCancel={() => setDisplayReflectCalculationWarning(false)}
          >
            <div>{t('指定のV3日程では量産日を超過します。自動計算結果を反映しますか？')}</div>
          </ConfirmModal>
        )}
        {/* 自動計算エラー */}
        {displayCalculateError && (
          <ErrorModal error={calculateError} onClickOk={() => setDisplayCalculateError(false)} />
        )}
        {/* 保存確認メッセージ */}
        {displayConfirmSavingMessage && (
          <ConfirmModal onClickCancel={() => setDisplayConfirmSavingMessage(false)} onClickOk={submit}>
            <div>{t('データを更新しますか？')}</div>
          </ConfirmModal>
        )}
        {/* 保存完了メッセージ */}
        {displayCompleteSavingMessage && (
          <OkModal onClickOk={() => setDisplayCompleteSavingMessage(false)}>
            <div>{t('保存しました。')}</div>
          </OkModal>
        )}
        {typeof addOrEditTargetSubScheduleIndex !== 'undefined' && (
          <SubScheduleFormModal
            data={
              addOrEditTargetSubScheduleIndex != null ? subScheduleItemsFields[addOrEditTargetSubScheduleIndex] : null
            }
            onClose={() => setAddOrEditTargetSubScheduleIndex(undefined)}
            save={(next) => {
              if (addOrEditTargetSubScheduleIndex == null) {
                appendSubScheduleItems(next);
              } else {
                updateSubScheduleItems(addOrEditTargetSubScheduleIndex, next);
              }
            }}
          />
        )}
      </form>
    );
  }
);

export interface ProductionsScheduleTabHandler {
  refresh: () => void;
}

interface Props {
  productionId: number;
  productionDivisionId: number;
  masters: ProductionMasters;
  editModeTabIndex: number | undefined;
  setEditable: (editable: boolean) => void;
}

/**
 * 日程編集タブコンポーネント（日程データの取得を行う）
 */
const ProductionScheduleTab = forwardRef<ProductionsScheduleTabHandler, Props>(
  ({ productionId, productionDivisionId, masters, editModeTabIndex, setEditable }, ref) => {
    // 言語情報
    const {
      i18n: { language },
    } = useTranslation();

    // データ取得
    const options = useDefaultOptions(language);
    const { data, isLoading, isError, error, refetch, isFetching } = useGetScheduleDetail(productionId, options);

    // メインエリア Ref
    const refMainArea = useRef<MainAreaHandler>(null);

    // フェッチ完了したらメイン部分をリフレッシュ
    useEffect(() => {
      if (!isFetching) {
        refMainArea.current?.refresh();
      }
    }, [isFetching]);

    // 親コンポーネントからの操作
    useImperativeHandle(ref, () => ({
      refresh: () => {
        void refetch();
      },
    }));

    // 待ち状態
    if (isLoading) {
      return <WaitComponent />;
    }

    // エラー表示
    if (isError) {
      return <ErrorComponent error={error} />;
    }

    // データが不足している場合
    if (masters.schedule_divisions == null || masters.holidays == null) {
      return <NoDataComponent />;
    }

    return (
      <MainArea
        ref={refMainArea}
        productionId={productionId}
        productionDivisionId={productionDivisionId}
        data={data.data}
        scheduleDevisions={masters.schedule_divisions}
        holidays={masters.holidays}
        editModeTabIndex={editModeTabIndex}
        setEditable={setEditable}
        refetch={async () => {
          await refetch();
        }}
      />
    );
  }
);

export default ProductionScheduleTab;
